import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { AuthMode, ChargedTotals, Credential, OnsiteProduct, OnsitePrice, TrialDiscount } from '@/types';
import type { IdentityMethod } from '@/api/client';
import { getIdentityAdapter } from '@/identityAdapter';
import { isValidEmail, suggestEmailCorrection } from '@/email';
import { useEmailSuggestion } from '@/useEmailSuggestion';
import { useEmailVerification } from '@/useEmailVerification';
import { CheckoutShell } from '@/components/CheckoutShell';
import { GoogleSignInButton } from '@/components/GoogleSignInButton';
import { SocialSignInButtons } from '@/components/SocialSignInButtons';
import type { SocialProvider } from '@/ensureEzAuth';
import { OrderSummary } from '@/components/OrderSummary';
import { t, useT } from '@/i18n/t';

interface Props {
  // The catalog product/price the order summary previews. Absent until the
  // config fetch lands (or when it fails); the summary then defers the amount
  // to the live session at the payment step.
  product?: OnsiteProduct;
  price?: OnsitePrice;
  // What the visitor is buying, named in the gate subtitle ("…to buy X").
  // Absent until the config fetch lands; the subtitle then stays generic.
  purchaseName?: string;
  amountCentsOverride?: number;
  // Set when the gate precedes a trial: the order summary then previews $0.00
  // due today and the recurring price after the free days.
  trialPeriodDays?: number;
  // A campaign code's discount on the gated price (the config promo block) and
  // its server-computed first charge: the order summary shows the discount
  // line, the discounted total and what later charges cost. Absent for
  // uncoded mounts.
  discount?: TrialDiscount | null;
  charged?: ChargedTotals | null;
  // The domain's auth mode + id, used to drive the identity gate's login/register
  // through the right adapter (ezAuth in-page form vs BYO navigate-away).
  authMode?: AuthMode;
  domainId?: number;
  // Whether the publisher allows checkout without an account. When false the gate
  // hides "Continue as guest".
  guestCheckoutEnabled: boolean;
  // 'checkout' (default) shows the full identity gate. 'login' opens the adapter's
  // in-page login form directly — used by "Already subscribed? Log in", where the
  // visitor wants to sign in, not choose between accounts and guest.
  intent?: 'checkout' | 'login';
  // Set when returning to the gate from payment after a guest checkout: opens
  // directly on that view (the guest email form) instead of the "Sign in to
  // continue" choices. Mount-time seed only.
  initialView?: GateView;
  // Prefills the guest email field; paired with initialView 'guest'.
  initialEmail?: string;
  // Records the visitor's gate selection (identity_method_selected) via the
  // parent flow, which owns attribution and the first-selection-per-mount
  // guard. Absent on standalone login surfaces (intent="login"), which are
  // outside the checkout funnel and never record a selection.
  recordIdentityMethod?: (method: IdentityMethod) => void;
  // Guest path: the visitor proceeds with an SM-collected email.
  onSubmit: (email: string) => void;
  // Logged-in path: an in-page login/register resolved a credential.
  onAuthenticated: (credential: Credential) => void;
  // Magic-link handoff. On the passwordless-first standalone login (intent
  // "login") it is the primary submit and carries the typed email; on the
  // checkout-gate login form it is the "Email me a sign-in link" fallback link
  // and carries none (ezAuth owns that form's email field). Omitted where
  // there is no magic-link wiring.
  onMagicLink?: (email?: string) => void;
  // Called before a BYO navigate-away (the publisher's goToLogin /
  // goToCreateAccount) so the flow can persist a resume intent. Never fires for
  // ezAuth's in-page login/register.
  onExternalAuth?: () => void;
  // Omitted when there is no earlier step to return to (a per-item purchase
  // opens straight on the gate); the Back button is then hidden.
  onBack?: () => void;
  onDismiss?: () => void;
  onDismissRequested?: () => void;
  loading: boolean;
  error: string | null;
}

// Which face of the gate is showing: the choice of login/register/guest, the
// mounted ezAuth form, the guest email field, or the post-auth transitional
// state shown while the parent establishes a session and starts checkout.
type GateView = 'choices' | 'auth' | 'guest' | 'completing';

// The guest verify starts after this typing pause, so the verdict is usually
// settled by the time Continue is pressed.
const verifyPrefetchDelayMs = 700;
// Longest Continue waits on an unsettled verdict before proceeding fail-open.
// A miss is a ~1s MillionVerifier lookup (p50); ~80% of read-API handlers finish
// within 2s (browser round trip makes the effective share lower).
const verifyWaitCapMs = 2000;

// A donation buys nothing, so its choices never say "buy"/"purchase". The
// guest wording only shows when the "Continue as guest" button does.
function choicesSubtitle(product: OnsiteProduct | undefined, purchaseName: string | undefined, guestCheckoutEnabled: boolean): string {
  if (product?.type === 'donation') {
    return guestCheckoutEnabled ? t('emailStep.subtitle.donation') : t('emailStep.subtitle.signInOnly.donation');
  }
  if (purchaseName) {
    return guestCheckoutEnabled ? t('emailStep.subtitle.named', { purchaseName }) : t('emailStep.subtitle.signInOnly.named', { purchaseName });
  }
  return guestCheckoutEnabled ? t('emailStep.subtitle.generic') : t('emailStep.subtitle.signInOnly.generic');
}

// The guest submit label is trial-aware: a trial gate (trialPeriodDays set)
// advances to "start trial" rather than "payment", since no charge happens yet.
function guestCtaLabel(loading: boolean, trialPeriodDays?: number): string {
  if (loading) {
    return trialPeriodDays ? t('emailStep.guestCta.trialLoading') : t('emailStep.guestCta.paymentLoading');
  }
  return trialPeriodDays ? t('emailStep.guestCta.trial') : t('emailStep.guestCta.payment');
}

export function EmailStep({ product, price, purchaseName, amountCentsOverride, trialPeriodDays, discount, charged, authMode, domainId, guestCheckoutEnabled, intent = 'checkout', initialView, initialEmail, recordIdentityMethod, onSubmit, onAuthenticated, onMagicLink, onExternalAuth, onBack, onDismiss, onDismissRequested, loading, error }: Props) {
  useT();
  const adapter = useMemo(() => getIdentityAdapter(authMode, domainId), [authMode, domainId]);
  // 'login' intent jumps straight to the mounted login form; an explicit
  // initialView (back-from-payment) opens on that view; otherwise the gate
  // opens on its choices.
  const [view, setView] = useState<GateView>(initialView ?? (intent === 'login' ? 'auth' : 'choices'));
  const [formMode, setFormMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState(initialEmail ?? '');
  // Client-side rejection of a malformed email (e.g. TLD-less) on the guest and
  // magic-link forms, shown in place of the parent's backend error so the
  // visitor gets feedback without a round trip. Cleared on edit and on a valid
  // submit.
  const [emailError, setEmailError] = useState<string | null>(null);
  // Deliverability gate on the guest path: while a verifyEmail call is in
  // flight, and the address a warn verdict was shown for (a second submit of
  // that same address is the visitor's "continue anyway"). Advisory only —
  // deliverability never blocks a checkout.
  const [verifying, setVerifying] = useState(false);
  const [warnedFor, setWarnedFor] = useState<string | null>(null);
  const emailVerification = useEmailVerification();
  // Verify a complete-looking guest address once typing pauses. The verdict is
  // held, not shown: the warning only renders from handleGuestSubmit, so a
  // mid-edit address never flashes it. A typo'd domain needs no vendor call —
  // the hint already names the fix.
  useEffect(() => {
    const trimmed = email.trim();
    if (view !== 'guest' || !isValidEmail(trimmed) || suggestEmailCorrection(trimmed)) {
      return;
    }
    const timer = setTimeout(() => emailVerification.prefetch(trimmed), verifyPrefetchDelayMs);
    return () => clearTimeout(timer);
  }, [email, view]);
  // The screen ezAuth's mounted form is showing, from its onScreenChange.
  // Derived: form screens (login/register) get our title + options; 'forgot'
  // gets our title + chevron but not the options; anything else (reset/verify/
  // notice) is ezAuth-owned chrome.
  const [ezAuthScreen, setEzAuthScreen] = useState<string>('login');
  const ezAuthOnFormScreen = ezAuthScreen === 'login' || ezAuthScreen === 'register';
  // Display mode from ezAuth's onScreenChange. Distinct from formMode (the
  // initial mount choice): when register hits account_exists, ezAuth's "Sign in
  // instead" link switches to login in place — flipping this without remounting
  // so the prefilled email survives. Driving title/loginOptions off formMode
  // would remount and wipe it.
  const ezAuthFormMode: 'login' | 'register' = ezAuthScreen === 'register' ? 'register' : 'login';
  // True once the "Continue with Google" / social buttons are actually on
  // screen; gates the "or" divider between them and the email path so a lone
  // divider never shows on domains without any SSO provider enabled.
  const [googleAvailable, setGoogleAvailable] = useState(false);
  const [socialAvailable, setSocialAvailable] = useState(false);
  const ssoAvailable = googleAvailable || socialAvailable;
  // Standalone login (intent "login") is passwordless-first: the magic-link
  // email form leads and the ezAuth password form stays unmounted until the
  // visitor asks for it via "Have a password? Sign in".
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  // Bumped to force a fresh ezAuth mount — the chevron on the forgot screen
  // uses it to return to the sign-in form (the facade exposes no navigate
  // method, and a fresh mount is exactly what ezAuth's own back does).
  const [formResetKey, setFormResetKey] = useState(0);
  const authContainerRef = useRef<HTMLDivElement | null>(null);
  // Synchronous guard against double credential resolution: the auth-view
  // Google path can fire twice (direct callback + the mounted prompt's
  // onChange), and a second onAuthenticated would double-start checkout.
  const completingRef = useRef(false);

  // Single chokepoint for gate-selection recording. Every selection action
  // (login/create-account/guest click, Google credential resolution) goes
  // through here; the parent callback dedupes to the first selection per flow
  // mount, and is absent entirely on standalone login surfaces.
  const recordSelection = (method: IdentityMethod) => {
    recordIdentityMethod?.(method);
  };

  // Single entry point for a resolved credential: hold the gate on the
  // transitional 'completing' view until the parent routes away (payment,
  // success, error) or reports an expired credential via the error prop.
  const completeWithCredential = (credential: Credential) => {
    if (completingRef.current) {
      return;
    }
    completingRef.current = true;
    setView('completing');
    onAuthenticated(credential);
  };

  // Social sign-in records at resolution like Google: the popup flow can be
  // cancelled, so the click alone is not a selection.
  const completeWithSocial = (credential: Credential, provider: SocialProvider) => {
    recordSelection(provider);
    completeWithCredential(credential);
  };

  // The only way 'completing' coexists with an error is reIdentify: the parent
  // keeps the gate mounted and sets the error prop. Fall back to the choices
  // view, which renders it inline (every other outcome unmounts this step).
  useEffect(() => {
    if (view === 'completing' && error) {
      completingRef.current = false;
      setView('choices');
    }
  }, [view, error]);

  // Mount ezAuth's in-page form while the 'auth' view is showing; resolve the
  // credential when the visitor authenticates, or return to the choices if the
  // prompt is closed (Back) or fails. Tear down on Back / unmount. On the
  // passwordless-first login the form host only renders once showPasswordForm
  // is set, so the mount waits for it.
  useEffect(() => {
    if (view !== 'auth') {
      return;
    }
    const target = authContainerRef.current;
    if (!target) {
      return;
    }
    let active = true;
    // Reset display state to the mount choice; ezAuth may flip it in place
    // later via onScreenChange (account_exists sign-in link → login, forgot,
    // etc.).
    setEzAuthScreen(formMode);
    const onScreenChange = (screen: string) => {
      if (!active) {
        return;
      }
      setEzAuthScreen(screen);
    };
    const promptOpts = { onScreenChange };
    const prompt = formMode === 'register'
      ? adapter.promptRegister(target, promptOpts)
      : adapter.promptLogin(target, promptOpts);
    prompt.completed.then((credential) => {
      if (!active) {
        return;
      }
      // A resolved credential hands off to the transitional view. A null
      // resolution (form closed/failed) drops back to the choices — or, on a
      // passwordless-wired standalone login, collapses the password form back
      // to the passwordless screen: there are no choices behind it, and its
      // magic link is the working path when ezAuth can't mount. Without the
      // magic-link wiring the password form is the only path, so keep the
      // choices fallback there.
      if (credential) {
        completeWithCredential(credential);
        return;
      }
      if (intent === 'login' && onMagicLink) {
        setEzAuthScreen(formMode);
        setShowPasswordForm(false);
        return;
      }
      setView('choices');
    });

    return () => {
      active = false;
      prompt.close();
    };
  }, [view, formMode, showPasswordForm, formResetKey]);

  const handleLogin = () => {
    // The selection is recorded at the click for both modes — the visitor's
    // commitment level is the same whether the form mounts in-page (ezAuth) or
    // the page unloads to publisher auth (BYO). Form bails show up as
    // post-selection attrition against payment_step_shown.
    recordSelection('login');
    if (adapter.capabilities.loginInPage) {
      setFormMode('login');
      setEzAuthScreen('login');
      setView('auth');
      return;
    }
    onExternalAuth?.();
    adapter.promptLogin();
  };

  const handleCreateAccount = () => {
    // Same click-time recording as handleLogin. The method is the choice at
    // click: ezAuth's in-place account_exists → "Sign in instead" flip
    // (onScreenChange) doesn't update it, so a register-clicked-then-logged-in
    // visitor counts as create_account — defensible and low-volume.
    recordSelection('create_account');
    if (adapter.capabilities.loginInPage) {
      setFormMode('register');
      setEzAuthScreen('register');
      setView('auth');
      return;
    }
    onExternalAuth?.();
    adapter.promptRegister();
  };

  const handleGuestSubmit = async (e: Event) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setEmailError(t('emailField.invalid'));
      return;
    }
    setEmailError(null);
    // Pre-payment deliverability check: a warn verdict pauses the submit once
    // with an inline warning; resubmitting the same address is "continue
    // anyway". The wait is bounded and fails open, so a slow lookup, an outage,
    // or a rate limit never blocks the checkout.
    if (verifying) {
      return;
    }
    if (warnedFor !== trimmed) {
      // An address with an obvious typo fix needs no vendor call: the hint is
      // already on screen naming the fix — pause on that alone.
      if (suggestEmailCorrection(trimmed)) {
        setWarnedFor(trimmed);
        return;
      }
      setVerifying(true);
      const verdict = await emailVerification.verdictWithin(trimmed, verifyWaitCapMs);
      setVerifying(false);
      if (verdict === 'warn') {
        setWarnedFor(trimmed);
        return;
      }
    }
    // No identity event here: the guest selection was recorded at the
    // "Continue as guest" click; a rejected email is post-selection attrition.
    onSubmit(trimmed);
  };

  // The passwordless-first login's primary action: hand the typed email to the
  // parent's magic-link send. Same inline shape check as the guest form.
  const handleMagicLinkSubmit = (e: Event) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setEmailError(t('emailField.invalid'));
      return;
    }
    setEmailError(null);
    onMagicLink?.(trimmed);
  };

  // Non-blocking domain-typo hint ("gmail.con" → "Did you mean gmail.com?").
  // The submit path above still accepts the typed address — the hint only
  // offers a one-click fix. Revealed on blur, hidden on focus.
  const { suggestion: emailSuggestion, revealSuggestion, hideSuggestion } = useEmailSuggestion(email);
  const emailInputRef = useRef<HTMLInputElement>(null);

  const sidebar = <OrderSummary product={product} price={price} amountCentsOverride={amountCentsOverride} trialPeriodDays={trialPeriodDays} discount={discount} charged={charged} />;

  if (view === 'auth') {
    // In 'login' intent there are no choices behind the form, so Back leaves the
    // gate entirely; otherwise it returns to the choices.
    const onBackFromAuth = intent === 'login' ? onBack : () => setView('choices');
    // A standalone login (login intent) is not a checkout: drop the order-summary
    // sidebar, the Stripe footer, and the 3-step indicator so it reads as a plain
    // sign-in. The checkout-gate login (intent 'checkout', reached mid-checkout)
    // keeps that chrome.
    const standaloneLogin = intent === 'login';
    // Use ezAuthFormMode (not formMode) so an in-place account_exists → login
    // switch (via ezAuth's "Sign in instead" link) updates chrome without
    // remounting the form (and wiping email).
    const loginOptions = ezAuthFormMode === 'login';
    // The standalone login is passwordless-first: the magic-link email form is
    // the primary action (a returning guest/Google visitor has no password, so
    // the password form is a dead end for exactly the cohorts this screen
    // serves) and the ezAuth password form mounts only behind "Have a
    // password? Sign in". Requires the magic-link wiring; without it the
    // password form stays primary. The checkout-gate login form keeps the
    // password-first layout.
    const passwordlessFirst = standaloneLogin && !!onMagicLink && adapter.capabilities.loginInPage;
    // The title tracks the visible auth path: the magic-link screen reframes
    // for the no-password cohort ("Access your purchase"); the password form
    // and ezAuth's forgot screen name the credential action.
    let title = t('emailStep.title.login');
    if (passwordlessFirst && !showPasswordForm) {
      title = t('emailStep.title.access');
    }
    if (ezAuthFormMode === 'register') {
      title = t('emailStep.title.register');
    }
    if (ezAuthScreen === 'forgot') {
      title = t('emailStep.title.reset');
    }
    // On ezAuth's forgot screen the chevron returns to the sign-in form (a
    // fresh mount — the facade exposes no navigate method, and a fresh mount is
    // exactly what ezAuth's own back does); elsewhere it is the parent back.
    const headBack = ezAuthScreen === 'forgot' ? () => setFormResetKey((n) => n + 1) : onBackFromAuth;
    const backButton = headBack && (
      <button type="button" class="sm-back" aria-label={t('common.back')} onClick={headBack}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
    );
    // Our head (chevron + title) shows on the form screens and the forgot
    // screen; ezAuth's reset/verify/notice screens own their whole chrome.
    const showOwnHead = ezAuthOnFormScreen || ezAuthScreen === 'forgot';
    // "Remember your password? Log in" — the standard forgot-screen escape back
    // to the sign-in form (a fresh mount, same as the chevron there). The whole
    // line is one tap target; the action word carries the accent, no underline.
    const rememberLogin = ezAuthScreen === 'forgot' && (
      <button class="sm-remember-login" type="button" onClick={() => setFormResetKey((n) => n + 1)}>
        {t('emailStep.rememberPassword')} <span class="sm-remember-login-action">{t('emailStep.title.login')}</span>
      </button>
    );
    return (
      <CheckoutShell
        ariaLabel={standaloneLogin ? title : t('emailStep.aria.signInForm')}
        currentStep={1}
        sidebar={standaloneLogin ? undefined : sidebar}
        narrow
        hideSteps={standaloneLogin}
        // Standalone login renders its back control inline with the title (below), so
        // the shell skips the empty head row that would otherwise float above it. On
        // ezAuth's reset/verify/notice screens our head steps aside entirely —
        // ezAuth renders its own heading and back button there.
        onBack={standaloneLogin ? undefined : headBack}
        onDismiss={onDismiss} onDismissRequested={onDismissRequested}
      >
        {passwordlessFirst ? (
          <>
            {showOwnHead && (
              <div class="sm-login-head">
                {backButton}
                <h2 class="sm-title">{title}</h2>
              </div>
            )}
            <div class="sm-login-options">
              {ezAuthOnFormScreen && !showPasswordForm && (
                <>
                  <p class="sm-subtitle">{t('emailStep.subtitle.access')}</p>
                  <form class="sm-form" onSubmit={handleMagicLinkSubmit}>
                    <label class="sm-field">
                      <span>{t('emailField.label')}</span>
                      <input
                        ref={emailInputRef}
                        type="email"
                        class="sm-input"
                        placeholder={t('emailField.placeholder')}
                        value={email}
                        onInput={(e) => {
                          setEmail((e.target as HTMLInputElement).value);
                          setEmailError(null);
                        }}
                        onBlur={revealSuggestion}
                        onFocus={hideSuggestion}
                        required
                        autofocus
                        disabled={loading}
                        aria-label={t('emailField.label')}
                      />
                    </label>
                    {emailSuggestion && (
                      <p class="sm-hint">
                        {t('emailField.didYouMean')}{' '}
                        <button
                          type="button"
                          class="sm-hint-address"
                          onClick={() => {
                            setEmail(emailSuggestion);
                            setEmailError(null);
                            emailInputRef.current?.focus();
                          }}
                        >
                          {emailSuggestion}
                        </button>
                        ?
                      </p>
                    )}
                    {(emailError ?? error) && <p class="sm-error" role="alert">{emailError ?? error}</p>}
                    <button
                      type="submit"
                      class="sm-btn sm-btn-primary"
                      disabled={loading || !email.trim().includes('@')}
                    >
                      {loading && <span class="sm-btn-spinner" aria-hidden="true" />}
                      {t('emailStep.magicLink')}
                    </button>
                  </form>
                </>
              )}
              {/* The two auth paths are mutually exclusive: the magic-link email
                  form swaps out for the ezAuth password form (two email fields on
                  one screen read as a bug). The ezAuth host holds a fixed position
                  among its siblings once rendered — ezAuth's in-place screen flips
                  (forgot/reset) hide our chrome but must not remount the host and
                  wipe its state. */}
              {showPasswordForm && <div key={formResetKey} class="sm-ezauth" ref={authContainerRef} />}
              {/* Sign-in methods group together: primary form, "or", Google and
                  social. The swap link is footer chrome below them, not another
                  method between the form and SSO. */}
              {ezAuthOnFormScreen && ssoAvailable && <div class="sm-or"><span>{t('emailStep.or')}</span></div>}
              {ezAuthOnFormScreen && (
                <GoogleSignInButton
                  domainId={domainId}
                  onAuthenticated={(credential) => {
                    // No recordIdentityMethod on standalone login surfaces, so
                    // this is a no-op there; the checkout gate never renders
                    // this branch.
                    recordSelection('google');
                    completeWithCredential(credential);
                  }}
                  onAvailabilityChange={setGoogleAvailable}
                />
              )}
              {ezAuthOnFormScreen && (
                <SocialSignInButtons domainId={domainId} onAuthenticated={completeWithSocial} onAvailabilityChange={setSocialAvailable} />
              )}
              {rememberLogin}
              {ezAuthOnFormScreen && (
                <div class="sm-magiclink-center">
                  <button
                    class="sm-secondary-link"
                    type="button"
                    onClick={() => {
                      // Collapsing from ezAuth's forgot screen must reset the
                      // screen state too — the form unmounts without firing
                      // onScreenChange, and the magic-link form only renders on
                      // a form screen.
                      if (showPasswordForm) {
                        setEzAuthScreen('login');
                      }
                      setShowPasswordForm(!showPasswordForm);
                    }}
                  >
                    {showPasswordForm ? t('emailStep.magicLink') : t('emailStep.havePassword')}
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            {standaloneLogin ? (
              // On ezAuth's reset/verify/notice screens our head row steps aside
              // entirely: ezAuth renders its own heading and its own back button
              // there, so a lone SM back arrow would float above a duplicate title.
              showOwnHead && (
                <div class="sm-login-head">
                  {backButton}
                  <h2 class="sm-title">{title}</h2>
                </div>
              )
            ) : (
              showOwnHead && (
                <>
                  {ezAuthOnFormScreen && <div class="sm-eyebrow">{t('emailStep.eyebrow.account')}</div>}
                  <h2 class="sm-title">{title}</h2>
                </>
              )
            )}
            {/* Stable .sm-ezauth node across loginOptions flips — swapping the whole
                branch (wrapper vs bare) would remount the host and wipe ezAuth's
                in-place email prefill after the account_exists sign-in link. */}
            <div class={loginOptions ? 'sm-login-options' : undefined}>
              <div key={formResetKey} class="sm-ezauth" ref={authContainerRef} />
              {loginOptions && adapter.capabilities.loginInPage && ezAuthOnFormScreen && ssoAvailable && (
                <div class="sm-or"><span>{t('emailStep.or')}</span></div>
              )}
              {loginOptions && adapter.capabilities.loginInPage && ezAuthOnFormScreen && (
                <GoogleSignInButton
                  domainId={domainId}
                  onAuthenticated={(credential) => {
                    // Google records at resolution (the GIS iframe swallows the
                    // click). On this view a "Log in" click already recorded the
                    // selection; the parent's first-selection guard makes this a
                    // no-op then — and also dedupes a double-fired Google callback
                    // (direct + mounted prompt onChange).
                    recordSelection('google');
                    completeWithCredential(credential);
                  }}
                  onAvailabilityChange={setGoogleAvailable}
                />
              )}
              {loginOptions && adapter.capabilities.loginInPage && ezAuthOnFormScreen && (
                <SocialSignInButtons domainId={domainId} onAuthenticated={completeWithSocial} onAvailabilityChange={setSocialAvailable} />
              )}
            </div>
            {rememberLogin}
            {ezAuthOnFormScreen && onMagicLink && loginOptions && (
              // The fallback for no-password returners (guest checkout, Google) on
              // the password-first login form. Login-only: a sign-in link makes no
              // sense on the register form. Carries no email — ezAuth owns this
              // form's email field, so the magic-link step collects it fresh.
              <div class="sm-magiclink-center">
                <button class="sm-secondary-link" type="button" onClick={() => onMagicLink()}>
                  {t('emailStep.magicLink')}
                </button>
              </div>
            )}
          </>
        )}
      </CheckoutShell>
    );
  }

  if (view === 'completing') {
    // Transitional state while the parent establishes the session, checks the
    // entitlement, and starts checkout. No Back and no interactive elements:
    // the credential is already resolved and a mid-flight choice would start a
    // second checkout. Dismiss stays — a dismissible flow can close at any step.
    const standaloneLogin = intent === 'login';
    return (
      <CheckoutShell
        ariaLabel={t('emailStep.aria.signingIn')}
        currentStep={1}
        sidebar={standaloneLogin ? undefined : sidebar}
        narrow
        hideSteps={standaloneLogin}
        onDismiss={onDismiss} onDismissRequested={onDismissRequested}
      >
        <div class="sm-eyebrow">{t('emailStep.eyebrow.account')}</div>
        <h2 class="sm-title">{t('emailStep.signingIn')}</h2>
        <div class="sm-completing" role="status">
          <span class="sm-btn-spinner" aria-hidden="true" />
          {!standaloneLogin && <p class="sm-subtitle">{t('emailStep.settingUpCheckout')}</p>}
        </div>
      </CheckoutShell>
    );
  }

  if (view === 'guest') {
    // The deliverability warning shows while the warned address is still in
    // the field; editing the address clears it (the next submit re-verifies).
    // When the typo hint is showing it wins: both messages mean "this address
    // looks wrong" for the same root cause, and the hint is the one that
    // names the fix.
    const showVerifyWarning = !emailSuggestion && warnedFor !== null && warnedFor === email.trim();
    const showContinueAnyway = warnedFor !== null && warnedFor === email.trim();
    return (
      <CheckoutShell ariaLabel={t('emailStep.guest.title')} currentStep={1} sidebar={sidebar} narrow onBack={() => setView('choices')} backDisabled={loading} onDismiss={onDismiss} onDismissRequested={onDismissRequested}>
        <div class="sm-eyebrow">{t('emailStep.eyebrow.contact')}</div>
        <h2 class="sm-title">{t('emailStep.guest.title')}</h2>
        <p class="sm-subtitle">{t('emailStep.guest.subtitle')}</p>

        <form class="sm-form" onSubmit={handleGuestSubmit}>
          <label class="sm-field">
            <span>{t('emailField.label')}</span>
            <input
              ref={emailInputRef}
              type="email"
              class="sm-input"
              placeholder={t('emailField.placeholder')}
              value={email}
              onInput={(e) => {
                setEmail((e.target as HTMLInputElement).value);
                setEmailError(null);
                setWarnedFor(null);
              }}
              onBlur={revealSuggestion}
              onFocus={hideSuggestion}
              required
              autofocus
              disabled={loading}
              aria-label={t('emailField.label')}
            />
          </label>

          {emailSuggestion && (
            <p class="sm-hint">
              {t('emailField.didYouMean')}{' '}
              <button
                type="button"
                class="sm-hint-address"
                onClick={() => {
                  setEmail(emailSuggestion);
                  setEmailError(null);
                  setWarnedFor(null);
                  emailInputRef.current?.focus();
                }}
              >
                {emailSuggestion}
              </button>
              ?
            </p>
          )}

          {showVerifyWarning && <p class="sm-error sm-verify-warning" role="alert">{t('emailStep.verifyWarning')}</p>}
          {(emailError ?? error) && <p class="sm-error" role="alert">{emailError ?? error}</p>}

          <button
            type="submit"
            class="sm-btn sm-btn-primary"
            disabled={loading || verifying || !email.trim().includes('@')}
          >
            {(loading || verifying) && <span class="sm-btn-spinner" aria-hidden="true" />}
            {showContinueAnyway ? t('emailStep.continueAnyway') : guestCtaLabel(loading || verifying, trialPeriodDays)}
          </button>
        </form>
      </CheckoutShell>
    );
  }

  return (
    <CheckoutShell ariaLabel={t('emailStep.title.signIn')} currentStep={1} sidebar={sidebar} narrow onBack={onBack} backDisabled={loading} onDismiss={onDismiss} onDismissRequested={onDismissRequested}>
      <div class="sm-eyebrow">{t('emailStep.eyebrow.account')}</div>
      <h2 class="sm-title">{t('emailStep.title.signIn')}</h2>
      <p class="sm-subtitle">{choicesSubtitle(product, purchaseName, guestCheckoutEnabled)}</p>

      {error && <p class="sm-error" role="alert">{error}</p>}

      <div class="sm-choices">
        {/* SSO leads the sign-in methods: ezAuth-only "Continue with Google" that
            resolves the same credential as the email/password path. It self-hides
            when Google is unavailable and reports that so the "or" divider below only
            shows when there is a button to divide from. BYO mode (loginInPage false)
            owns its own SSO, so the button is not rendered there. */}
        {adapter.capabilities.loginInPage && (
          <GoogleSignInButton
            domainId={domainId}
            onAuthenticated={(credential) => {
              // Same resolution-time recording as the auth-view button above.
              recordSelection('google');
              completeWithCredential(credential);
            }}
            onAvailabilityChange={setGoogleAvailable}
          />
        )}
        {adapter.capabilities.loginInPage && (
          <SocialSignInButtons domainId={domainId} onAuthenticated={completeWithSocial} onAvailabilityChange={setSocialAvailable} />
        )}
        {adapter.capabilities.loginInPage && ssoAvailable && <div class="sm-or"><span>{t('emailStep.or')}</span></div>}

        <button class="sm-btn sm-btn-primary" type="button" onClick={handleLogin}>
          {t('emailStep.login')}
        </button>
        {guestCheckoutEnabled && (
          <button
            class="sm-btn sm-btn-secondary"
            type="button"
            onClick={() => {
              recordSelection('guest');
              // The hook's state survives the view switch; clear it so a hint
              // armed on a previous pass can't flash for a frame on re-entry.
              hideSuggestion();
              setView('guest');
            }}
          >
            {t('emailStep.continueAsGuest')}
          </button>
        )}
        {/* When guest checkout is disabled, Create account is the only path to an
            account, so it stays a full button here. When guest checkout is enabled it
            demotes to the quiet link below (accounts can also be created after
            purchase). */}
        {adapter.capabilities.canCreateAccount && !guestCheckoutEnabled && (
          <button class="sm-btn sm-btn-secondary" type="button" onClick={handleCreateAccount}>
            {t('emailStep.createAccount')}
          </button>
        )}
      </div>

      {adapter.capabilities.canCreateAccount && guestCheckoutEnabled && (
        <div class="sm-create-account">
          <button class="sm-secondary-link" type="button" onClick={handleCreateAccount}>
            {t('emailStep.createAccount')}
          </button>
        </div>
      )}
    </CheckoutShell>
  );
}
